import React from 'react';
import Translate from '~/components/Translate/Translate';
import { translate } from '~/modules/helpers/lang';
import { NODE_TYPES } from '~/modules/nodeTypes';
import { formatNodeTitle } from './setNodeValues';

/**
 * View element for placeholder
 * @param {String} label
 * @param {String} value
 * @param {String} labelClassName
 * @param {Boolean} localizeLabel
 * @returns {JSX.Element}
 * @author sst
 */
const placeholderView = (
    label,
    value,
    labelClassName = 'label',
    localizeLabel = true
) => {
    return (
        <div className="placeholder-value">
            <span className={labelClassName}>
                {localizeLabel ? <Translate message={label} /> : label}
            </span>
            <span className="value">{value}</span>
        </div>
    );
};

/**
 * Returns a placeholder label and value or JSX element.
 * @param {String} label
 * @param {String} value
 * @param {Boolean} returnAsString
 * @param {String} labelClassName
 * @param {Boolean} localizeLabel
 * @returns {Object|JSX.Element}
 */
const createPlaceholderLabelValueOrView = (
    label,
    value,
    returnAsString,
    labelClassName,
    localizeLabel = true
) => {
    if (returnAsString) {
        const translatedLabel = localizeLabel ? translate(label) : label;
        return {
            label: translatedLabel,
            value
        };
    }
    return placeholderView(label, value, labelClassName, localizeLabel);
};

/**
 * Convert placeholder to string
 * @param {String} value
 * @param {Object} context
 * @returns {JSX.Element|*}
 * @author sst
 */
export function getPlaceholderString(value, context, returnAsString = false) {
    let data = value.split('::');
    switch (data[0]) {
        case 'EDITED':
            if (returnAsString) {
                return data[1];
            }
            return <div className="value">{data[1]}</div>;
        case 'FLOW':
            let action = context.getNodeByActionId(data[1]);

            if (!action) return;

            //sst: compose title
            let title = action.get('actionTitle') || action.get('title');
            if (action.get('stepType') === NODE_TYPES.RUNBOOK) {
                title = action.get('name') || 'Playbook';
            } else if (action.get('stepType') === NODE_TYPES.USERCHOICE) {
                title = action.get('title') || 'User Choice';
            }

            //sst: get node title formatted (if there are a placeholder)
            title = formatNodeTitle(title, { plainText: true });

            const className =
                action.get('actionType') === NODE_TYPES.RUNBOOK
                    ? 'input-param'
                    : action.get('actionType').toLowerCase();

            return createPlaceholderLabelValueOrView(
                title,
                data[2],
                returnAsString,
                className,
                false
            );

        case 'INCIDENT':
            let incidentField = context.incidentFields.filter(field => {
                if (field.value === value) return field;
            });

            return createPlaceholderLabelValueOrView(
                'Incident field',
                incidentField[0]?.label,
                returnAsString
            );

        case 'CEF':
            let incidentArtifacts = context.incidentArtifacts.filter(field => {
                if (field.value === value) return field;
            });

            return createPlaceholderLabelValueOrView(
                'Artifact',
                incidentArtifacts[0]?.label,
                returnAsString
            );

        case 'SPECIAL':
            let internalValues = context.internalValues.filter(field => {
                if (field.value === value) return field;
            });

            return createPlaceholderLabelValueOrView(
                'Internal value',
                internalValues[0]?.label,
                returnAsString
            );

        case 'TRIAGE':
            let incidentTriage = context.incidentTriage.filter(field => {
                if (field.value === value) return field;
            });

            return createPlaceholderLabelValueOrView(
                SETTINGS.triageSectionName,
                incidentTriage[0]?.label,
                returnAsString,
                'label',
                false
            );

        case 'PLAYBOOK':
        case 'PLAYBOOKPAYLOAD':
            return createPlaceholderLabelValueOrView(
                'Playbook',
                data[1],
                returnAsString
            );
    }
}
